import OverlayView from "Components/PopUpOverlay";
import CoordMapType from "./CoordMapType";
import {
  calculateDirections,
  findShortestDistances,
  find3CirclesCenter,
} from "./coordinates_v3";
import YCLatLng from "./YCLatLng";
import MapMarker from "./MapMarker";

type OnClickFunction = () => void;
type Callback = (success: boolean, message: string) => void;

class GoogleMap {
  static MAP_TYPE = {
    SATELLITE: "satellite",
    ROADMAP: "roadmap",
    COORDINATE: "coordinate",
  };

  isCustomTiledMap = false;

  hasCustomTiles = false;

  selectedStop = null;

  stops = null;

  // this.trails = null;

  startingStop = null;

  coordinateMapType: CoordMapType = null;

  currentStop = null;

  tooltipOverlay = null;

  multiplyer = null;

  stopRange = 20;

  mapMarkers = [];

  hasPolygons = false;

  map = null;

  flightPaths = [];

  minCoords: google.maps.LatLng;

  maxCoords: google.maps.LatLng;

  minRealCoords: google.maps.LatLng;

  maxRealCoords: google.maps.LatLng;

  currentHoverStopId = -1;

  markersVisible = null;

  _currentAudioId = null;

  _msg = null;

  _firstRun = true;

  _directions = null; // directions controller

  _recenterOnAccurateGPSUpdate = true;

  isMobile = false;

  mapOptions: google.maps.MapOptions = {
    gestureHandling: "greedy",
    center: { lat: undefined, lng: undefined },
    minZoom: 9,
    zoom: 10,
    maxZoom: 11,
    panControl: false,
    draggingCursor: "move",
    mapTypeControl: false,
    streetViewControl: false,
    rotateControl: false,
    mapTypeId: GoogleMap.MAP_TYPE.ROADMAP,
    fullscreenControl: false,
    zoomControl: false,
    cameraControl: false,
    disableDoubleClickZoom: true,
    backgroundColor: "#FFFFFF",
    zoomControlOptions: {
      position: window.google.maps.ControlPosition.TOP_LEFT,
    },
    disableDefaultUI: true,
  };

  initMapOptions: google.maps.MapOptions;

  constructor(
    mapElement: HTMLElement,
    tiles: any,
    minCoords: any,
    maxCoords: any,
    mapMarkers: any[],
    mapOptions: any
  ) {
    mapMarkers.forEach((mm) => {
      this.mapMarkers.push(
        new MapMarker(mm.customLat, mm.customLng, mm.realLat, mm.realLng)
      );
    });
    // Custom map = has custom tiles, if so we focus on custm vs real lat lng
    this.isCustomTiledMap = tiles && tiles.length > 0;
    this.hasCustomTiles = this.isCustomTiledMap;

    this.mapOptions = { ...this.mapOptions, ...mapOptions };
    this.initMapOptions = this.mapOptions;

    if (minCoords) {
      this.minRealCoords = this.asGoogleLatLng(minCoords);
      this.minCoords = this.translatePoint(minCoords);
    }

    if (maxCoords) {
      this.maxRealCoords = this.asGoogleLatLng(maxCoords);
      this.maxCoords = this.translatePoint(maxCoords);
    }

    if (this.minCoords && this.maxCoords) {
      // TODO the backend should have bounds defined explicitly for Custom and Native maps.
      // this.mapOptions.restriction = {
      //   latLngBounds: {
      //     north: this.minCoords.lat(),
      //     south: this.maxCoords.lat(),
      //     west: this.minCoords.lng(),
      //     east: this.maxCoords.lng(),
      //   },
      // };
    }

    this.map = new window.google.maps.Map(mapElement, this.mapOptions);

    // overwrite double click behavior to do nothing
    window.google.maps.event.addDomListener(
      this.map,
      "dblclick",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (_: any) => {}
    );

    // Init our custom tiles
    if (this.isCustomTiledMap) {
      this.coordinateMapType = new CoordMapType(
        tiles,
        tiles[0].path,
        tiles[0].zoom, // min zoom
        tiles[tiles.length - 1].zoom, // max zoom
        tiles[0].version
      );
      // Now attach the coordinate map type to the map's registry.
      this.map.mapTypes.set(
        GoogleMap.MAP_TYPE.COORDINATE,
        this.coordinateMapType
      );
      this.map.setMapTypeId(GoogleMap.MAP_TYPE.COORDINATE);
      this.coordinateMapType.clearCache();
    }
  }

  setCenter = (latlng: any) => {
    this.map.setCenter(latlng);
  };

  panTo = (latlng: any) => {
    this.map.panTo(latlng);
  };

  setMapType = (maptype: string, destinations: [any]) => {
    // Do we need to actually change the map type
    if (maptype === this.map.mapTypeId) return;
    let requiresRedraw = false;
    // Coordinate = custom map
    if (GoogleMap.MAP_TYPE.COORDINATE === maptype) {
      // If we are switching too custom, lets redraw
      if (!this.isCustomTiledMap) {
        requiresRedraw = true;
        this.isCustomTiledMap = true;
      } else {
        // already custom map.
        return;
      }
    }
    // If we are already custom and switching to native, we need to redraw.
    else if (this.isCustomTiledMap) {
      this.isCustomTiledMap = false;
      requiresRedraw = true;
    }

    if (!requiresRedraw) {
      this.map.setMapTypeId(maptype);
      return;
    }

    if (this.isCustomTiledMap) {
      const newMapOptions = {
        ...this.initMapOptions,
      };
      newMapOptions.mapTypeId = maptype;
      this.map.setOptions(newMapOptions);
    } else {
      // TODO this center should be derived from real center, rather mapMarkers
      const newMapOptions = {
        ...this.initMapOptions,
      };
      newMapOptions.zoom = 16;
      newMapOptions.maxZoom = 18;
      newMapOptions.mapTypeId = maptype;
      if (this.mapMarkers.length > 0) {
        const mI: number = Math.floor(this.mapMarkers.length / 2);
        const markerCenter = this.asGoogleLatLng(this.mapMarkers[mI].realPoint);
        newMapOptions.center = markerCenter;
      }
      this.map.setOptions(newMapOptions);
      // this is weird, we need to set maxZoom, before we can change and set minZoon.
      newMapOptions.minZoom = 12;
      this.map.setOptions(newMapOptions);
    }

    destinations.forEach((destination: any) => {
      const position = this.isCustomTiledMap
        ? new window.google.maps.LatLng(
            destination.customLat,
            destination.customLng
          )
        : new window.google.maps.LatLng(
            destination.realLat,
            destination.realLng
          );

      const { marker, overlay, polygon } = destination;
      const offset = 0.00012;
      Object.keys(marker).forEach((key, index) => {
        const offsetPosition = {
          lat: position.lat() + offset * index,
          lng: position.lng() + offset * index,
        };
        marker[key].setOptions({ ...marker[key], position: offsetPosition });
      });
      overlay.position = position;

      const shape = this.isCustomTiledMap
        ? destination.custom_shape
        : destination.real_shape;
      if (shape) {
        const shapeMap = this.createPathMap(shape);
        if (polygon) {
          polygon.forEach((polyShape) => {
            polyShape.setOptions({
              ...polyShape,
              paths: shapeMap[polyShape.type],
            });
          });
        }
      }
    });
  };

  getZoom = () => {
    return this.map.getZoom();
  };

  setZoom = (zoom: number) => {
    this.map.setZoom(zoom);
  };

  createOverlayElement = (destination: any, onClick: OnClickFunction) => {
    // Wrapper element
    const container = document.createElement("div"); // div.container
    container.setAttribute("id", `stop-${destination.id}`);
    container.setAttribute("data-scene-state", "DEFAULT");
    container.setAttribute("data-stop-name", destination.title);
    container.setAttribute("style", "position: absolute");
    container.addEventListener("click", onClick);
    return container;
  };

  drawPolygon = (polyOptions: any, onClick: OnClickFunction) => {
    const polygons = [];
    const pathMap = this.createPathMap(polyOptions?.paths);
    const { map } = this;

    // iterate over keys of paths and create polygons
    Object.keys(pathMap).forEach((key) => {
      const polygon = new window.google.maps.Polygon({
        id: polyOptions?.id,
        paths: pathMap[key],
        strokeColor: polyOptions.drawOptions[key]?.strokeColor,
        strokeWeight: polyOptions.drawOptions[key]?.strokeWeight || 1,
        fillColor: polyOptions.drawOptions[key]?.fillColor,
        type: key,
      });
      polygon.addListener("click", onClick);
      polygon.setMap(map);
      polygons.push(polygon);
    });
    return polygons;
  };

  drawMarker = (markerOption: any, onClick: OnClickFunction) => {
    const marker = new window.google.maps.Marker(markerOption);
    marker.addListener("click", onClick);
    marker.setMap(this.map);
    return marker;
  };

  drawDestination = (
    destination: any,
    drawOptions: any,
    onClick: OnClickFunction
  ) => {
    let polygon = null;
    let overlay: OverlayView = null;

    const position = this.isCustomTiledMap
      ? new window.google.maps.LatLng(
          destination.customLat,
          destination.customLng
        )
      : new window.google.maps.LatLng(destination.realLat, destination.realLng);

    const shape = this.isCustomTiledMap
      ? destination.custom_shape
      : destination.real_shape;

    const pathMap = this.createPathMap(shape);
    if (Array.isArray(shape) && shape.length > 0) {
      polygon = this.drawPolygon(
        {
          id: destination.id,
          paths: shape,
          drawOptions,
        },
        onClick
      );
    }
    const markerMap = {};
    const offset = 0.00012;
    Object.keys(pathMap).forEach((key, index) => {
      const offsetPosition = {
        lat: position.lat() + offset * index,
        lng: position.lng() + offset * index,
      };
      const marker = this.drawMarker(
        {
          position: offsetPosition,
          icon: drawOptions[key]?.marker || drawOptions.institution?.marker,
        },
        onClick
      );
      markerMap[key] = marker;
    });

    if (destination.realLat !== 0 && destination.realLng !== 0) {
      overlay = new OverlayView({
        position,
        content: this.createOverlayElement(destination, onClick),
      });
      overlay.setMap(this.map);
    }
    return {
      polygon,
      marker: markerMap,
      overlay,
      map: this.map,
    };
  };

  getLongestIndex = (list) => {
    let i;
    let biggestIndex = 0;
    for (i = 1; i < list.length; i += 1) {
      if (list[i].distance > list[biggestIndex].distance) {
        biggestIndex = i;
      }
    }
    return biggestIndex;
  };

  isInBounds = (pt) => {
    if (!this.minCoords || !this.maxCoords) return true;
    return (
      pt.lat() < (this.minCoords?.lat ?? 0) &&
      pt.lng() > (this.minCoords?.lng ?? 0) &&
      pt.lat() > (this.maxCoords?.lat ?? 0) &&
      pt.lng() < (this.maxCoords?.lng ?? 0)
    );
  };

  toGoogleLatLng = (latY: any, latX: any) => {
    return new window.google.maps.LatLng(latY, latX);
  };

  asGoogleLatLng = (point: any) => {
    if (!point) return point;
    if (typeof point.x === "number")
      return this.toGoogleLatLng(point.y, point.x);
    if (typeof point?.lat !== "function")
      return this.toGoogleLatLng(point.lat, point.lng);
    return point;
  };

  asYCLatLng = (point: any) => {
    if (!point) return point;
    if (typeof point.lat !== "function")
      return new YCLatLng(point.lat, point.lng);
    return new YCLatLng(point.lat(), point.lng());
  };

  /**
   * Translate Point to Custom Map if applicable
   * @param point
   * @returns translated point
   */
  translatePoint = (point: any): google.maps.LatLng => {
    if (this.isCustomTiledMap) {
      const markers = findShortestDistances(
        this.asYCLatLng(point),
        this.mapMarkers
      );
      const customPoint = find3CirclesCenter(markers, 1);
      return this.asGoogleLatLng(customPoint);
    }
    return this.asGoogleLatLng(point);
  };

  clearCurrentDirections = () => {
    // remove current flight paths from map
    this.flightPaths.forEach((flightPath) => {
      flightPath.setMap(null);
    });
  };

  getDirections = (
    startPoint: google.maps.LatLng,
    stopoverPoints: google.maps.LatLng[],
    destPoint: google.maps.LatLng,
    callback: Callback
  ) => {
    // TODO: Make bounds work properly for interactive map
    // if (!this.isInBounds(startPoint)) {
    //   callback(false,'Start point is outside location bounds.')
    //   return
    // }
    // if (!this.isInBounds(destPoint)) {
    //   callback(false,'Destination point is outside location bounds.')
    //   return
    // }
    calculateDirections(
      startPoint,
      stopoverPoints,
      destPoint,
      (paths: any[]) => {
        const polyLines = paths.map((path: any, index) => {
          const subpathPolyLines = path.map((subpath) =>
            this.translatePoint({
              lat: subpath.lat(),
              lng: subpath.lng(),
            })
          );

          // add start and end points for the first subpath
          if (index === 0) {
            const result = [
              this.translatePoint(startPoint),
              ...subpathPolyLines,
            ];
            if (stopoverPoints[0]) {
              result.push(this.translatePoint(stopoverPoints[0]));
            } else if (paths.length === 1) {
              // only one path means no stopover points, so append destPoint
              result.push(this.translatePoint(destPoint));
            }
            return result;
          }

          // add start and end points for the last subpath
          if (index === paths.length - 1) {
            const result = stopoverPoints[index - 1]
              ? [this.translatePoint(stopoverPoints[index - 1])]
              : [];
            result.push(...subpathPolyLines, this.translatePoint(destPoint));
            return result;
          }

          // add start and end points for waypoint paths
          const result = stopoverPoints[index - 1]
            ? [this.translatePoint(stopoverPoints[index - 1])]
            : [];
          result.push(...subpathPolyLines);
          if (stopoverPoints[index]) {
            result.push(this.translatePoint(stopoverPoints[index]));
          }
          return result;
        });

        const sym = {
          path: "M 2,0 -2,0 0,-2z",
          strokeColor: "#085FE5",
          fillColor: "#085FE5",
          fillOpacity: 0.7,
          strokeWeight: 4,
          anchor: new window.google.maps.Point(0, 0),
        };

        polyLines.forEach((path) => {
          const flightPath = new window.google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: "#085FE5",
            strokeOpacity: 0.9,
            strokeWeight: 3,
            icons: [
              {
                icon: sym,

                offset: "25%",
              },

              {
                icon: sym,

                offset: "75%",
              },
            ],
          });

          flightPath.setMap(this.map);

          // Store the flightPath, so it can be destroy them later
          this.flightPaths.push(flightPath);
        });

        this.map.panTo(this.translatePoint(startPoint));
      },
      (message: string) => {
        callback(false, message);
      }
    );
  };

  // organize paths by type if stop contains multiple polygons
  createPathMap = (paths: any[]) => {
    const pathMap = {};
    if (Array.isArray(paths)) {
      paths.forEach((path) => {
        if (!pathMap[path.type]) {
          pathMap[path.type] = [path];
        } else {
          pathMap[path.type] = [...pathMap[path.type], path];
        }
      });
    }
    return pathMap;
  };

  destroy = () => {
    if (this.map) {
      this.map = null;
    }
    if (this.flightPaths) {
      this.flightPaths = null;
    }
  };
}

export default GoogleMap;
